import React, { useState, useEffect } from 'react';
import { 
    Paper, 
    Typography, 
    Button, 
    Alert, 
    Stack, 
    Box,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useTranslation } from 'react-i18next';

// --- Services & Utils ---
import { anonymizeFilePII } from '../../../services/piiService';
import { startAsyncAnonymization } from '../../../services/asyncAnonymizationService';
import { createBatchId } from '../../../shared/createBatchId';

// --- Components ---
import PIIFileDropzone from './PIIFileDropzone';
import PIIFileList from './PIIFileList';
import PIIAnonymizationOptionsDialog from './PIIAnonymizationOptionsDialog/PIIAnonymizationOptionsDialog';
import PreviewPDFEdit from './PreviewPDFEdit/PreviewPDFEdit';
import PreviewWord from './PreviewWord'; 
import ActiveOptionsDebugHint from './ActiveOptionsDebugHint';

const ACCEPTED_EXTENSIONS = ['.pdf', '.docx'];
const randomInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

const PIIFileUploadPanel = () => {
    const { t } = useTranslation();

    const [files, setFiles] = useState([]);
    const [error, setError] = useState(null);
    const [infoMessage, setInfoMessage] = useState('');
    const [isUploading, setIsUploading] = useState(false);

    const [options, setOptions] = useState({
        save_to_db: false,
        anonymize_birth_number: false,
        anonymize_birth_date: false,
        anonymize_names: false,
        exclude_contract_info_first_page: false,
        name_confidence_threshold: 60,
        anonymize_signature: false,
        anonymize_account_number: false,
        anonymize_address: false,
        anonymize_phone: false,
        anonymize_email: false,
        anonymize_passport_number: false,
        anonymize_id_number: false,
        text_anonymization_mode: 'random',
        signature_anonymization_mode: 'black_box',
        custom_words: [],
    });

    const [optionsOpen, setOptionsOpen] = useState(false);
    const [previewFile, setPreviewFile] = useState(null);

    const isDocx = previewFile?.name?.toLowerCase().endsWith('.docx');
    const hasAnySuccess = files.some((f) => f.status === 'success' && f.blob);

    const resetMessages = () => {
        setError(null);
        setInfoMessage('');
    };

    const isFileAccepted = (file) => {
        const lower = (file?.name || '').toLowerCase();
        return ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext));
    };

    const createFileItem = (file) => ({
        id: `${file.name}_${file.size}_${Date.now()}_${Math.random()}`,
        file,
        name: file.name,
        size: file.size || 0,
        status: 'pending',
        progress: 0,
        error: null,
        blob: null,
        plateau: randomInt(90, 97),
        speed: randomInt(1, 3),
        batchId: null,
    });

    const addFiles = (fileList) => {
        const newFiles = Array.from(fileList || []).filter(Boolean);
        if (!newFiles.length) return;

        const accepted = [];
        const rejected = [];

        newFiles.forEach((f) => {
            if (isFileAccepted(f)) accepted.push(createFileItem(f));
            else rejected.push(f);
        });

        if (accepted.length) setFiles((prev) => [...prev, ...accepted]);
        if (rejected.length) setError(t('admin.pii.fileTypeError', 'Podporované súbory: .pdf, .docx'));
        else setError(null);
    };

    const removeFile = (id) => {
        if (isUploading) return;
        setFiles((prev) => prev.filter((f) => f.id !== id));
    };

    useEffect(() => {
        const hasUploading = files.some((f) => f.status === 'uploading');
        if (!hasUploading) return;
        const intervalId = setInterval(() => {
            setFiles((prev) => prev.map((f) => {
                if (f.status !== 'uploading' || f.progress >= f.plateau) return f;
                return { ...f, progress: Math.min(f.plateau, (f.progress || 5) + f.speed) };
            }));
        }, 500);
        return () => clearInterval(intervalId);
    }, [files]);

    const processSingleFile = async (fileItem, currentOptions = {}) => {
        const { id, file } = fileItem;
        const isPdf = file.name.toLowerCase().endsWith('.pdf');

        setFiles((prev) => prev.map((f) => f.id === id ? { ...f, status: 'uploading', error: null, progress: 5 } : f));

        try {
            if (isPdf) {
                await startAsyncAnonymization(file, currentOptions, currentOptions.anonymizationGroupId);
                setFiles((prev) => prev.map((f) => f.id === id ? { ...f, status: 'queued', progress: 100, error: null, blob: null } : f));
            } else {
                const res = await anonymizeFilePII(file, currentOptions);
                if (!res.success) {
                    setFiles((prev) => prev.map((f) => f.id === id ? { ...f, status: 'error', progress: 100, error: res.error || t('admin.pii.anonymizeFailed', 'Nepodarilo sa anonymizovať súbor.'), blob: null } : f));
                    return;
                }
                setFiles((prev) => prev.map((f) => f.id === id ? { ...f, status: 'success', progress: 100, error: null, blob: res.data.blob, downloadName: `anonymizovany_${file.name}` } : f));
            }
        } catch (e) {
            setFiles((prev) => prev.map((f) => f.id === id ? { ...f, status: 'error', progress: 100, error: e?.message || t('admin.pii.anonymizeFailed', 'Nepodarilo sa anonymizovať súbor.'), blob: null } : f));
        }
    };

    const anonymizeAll = async (currentOptions = {}) => {
        if (!files.length) { setError(t('admin.pii.noFile', 'Najskôr prosím vyberte súbor.')); return; }
        resetMessages();
        const toProcess = files.filter((f) => f.status === 'pending' || f.status === 'error');
        if (!toProcess.length) { setInfoMessage(t('admin.pii.nothingToProcess', 'Nie sú žiadne súbory na spracovanie.')); return; }

        const batchId = createBatchId();
        const idsToProcess = new Set(toProcess.map((f) => f.id));
        setFiles((prev) => prev.map((f) => (idsToProcess.has(f.id) ? { ...f, batchId } : f)));
        setIsUploading(true);
        await Promise.all(toProcess.map((f) => processSingleFile(f, { ...currentOptions, anonymizationGroupId: batchId })));
        setIsUploading(false);
        setInfoMessage(t('admin.pii.filesFinished', 'Spracovanie bolo odoslané. Pri PDF súboroch skontrolujte spracovania vedľa.'));
    };

    const downloadFile = (fileItem) => {
        if (!fileItem.blob) return;
        const url = window.URL.createObjectURL(fileItem.blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileItem.downloadName || `anonymizovany_${fileItem.name || 'subor'}`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const downloadAll = () => files.filter((f) => f.status === 'success' && f.blob).forEach(downloadFile);

    const statusColor = (status) => ({ success: 'success', queued: 'secondary', error: 'error', uploading: 'info' }[status] || 'default');
    const statusLabel = (status) => ({ success: t('admin.pii.statusSuccess', 'Hotovo'), queued: t('admin.pii.statusQueued', 'Na pozadí'), error: t('admin.pii.statusError', 'Chyba'), uploading: t('admin.pii.statusUploading', 'Odosiela sa') }[status] || t('admin.pii.statusPending', 'Čaká'));

    return (
        <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{t('PIIFileUploadPanel.title', 'Nahrať súbory')}</Typography>
                <ActiveOptionsDebugHint options={options} />
            </Box>

            <Box sx={{ mb: 2 }}>
                <PIIFileDropzone disabled={isUploading} onFilesAdded={(fl) => { resetMessages(); addFiles(fl); }} />
            </Box>

            <Box sx={{ flexGrow: 1, mb: 2 }}>
                <PIIFileList files={files} isUploading={isUploading} statusColor={statusColor} statusLabel={statusLabel} onRemove={removeFile} onDownload={downloadFile} onPreview={setPreviewFile} />
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
            {infoMessage && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>{infoMessage}</Alert>}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 'auto' }}>
                <Button variant="contained" color="primary" fullWidth disableElevation disabled={isUploading || files.length === 0} onClick={() => setOptionsOpen(true)} sx={{ borderRadius: 2, py: 1, fontWeight: 600 }}>
                    {isUploading ? t('PIIFileUploadPanel.anonymizing', 'Anonymizujem...') : t('PIIFileUploadPanel.anonymizeFiles', 'Anonymizovať súbory')}
                </Button>
                <Button variant="outlined" fullWidth disabled={!hasAnySuccess} onClick={downloadAll} startIcon={<DownloadIcon />} sx={{ borderRadius: 2, py: 1, fontWeight: 600 }}>
                    {t('PIIFileUploadPanel.downloadAll', 'Stiahnuť všetko')}
                </Button>
            </Stack>

            <PIIAnonymizationOptionsDialog open={optionsOpen} options={options} onChangeOptions={setOptions} onClose={() => setOptionsOpen(false)} onConfirm={async () => { setOptionsOpen(false); await anonymizeAll(options); }} pseudoAnonymization={false} loading={isUploading} />
            {previewFile && (isDocx ? <PreviewWord open={!!previewFile} fileItem={previewFile} onClose={() => setPreviewFile(null)} /> : <PreviewPDFEdit open={!!previewFile} fileItem={previewFile} onClose={() => setPreviewFile(null)} />)}
        </Paper>
    );
};

export default PIIFileUploadPanel;